import { styled } from '@mui/material/styles';
import { useDebounce } from 'use-debounce';
import { useEffect, useState } from 'react';
import { PolicyDetails } from '@kw/service-definitions-eval';
import { useSearchParams, useLocation } from 'react-router-dom';
import {
  QContainer,
  QSelect,
  QDatePicker,
  QChip,
  QIconButton,
  QSwitch,
  QToggleButton,
  QToggleButtonGroup,
  QTooltip,
  QTypography,
} from '@kw/quokka-ui';
import dayjs from 'dayjs';
import { Box } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ApplicationsTable from './ApplicationsTable';
import { KwFilterWrapper } from '../../kw-ui-components/KwFilterWrapper';
import { ResetButton } from '../shared/ResetButton';
import { useAppVersions, useCategories, useHighRiskCountries } from './hooks';
import { useLocalStorage } from '../../utils/useLocalStorage';
import { useApplicationSortParams } from './useApplicationSortParams';
import { capitalize } from '../../utils/capitalize';
import { handleUserSelection } from '../../utils/handleUserSelection';
import { BackgroundProgressBar } from '../BackgroundProgressBar/BackgroundProgressBar';
import { useReloadRestore } from './GuardianReport/utils/useReloadRestore';
import { useFetchService } from '../../utils/fetchService';

const hasQueryParamValue = (value: unknown): boolean => value !== null && typeof value !== 'undefined' && value !== '';

const compactAppListParams = (params: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(params)
      .map(([key, value]) => [key, Array.isArray(value) ? value.filter(hasQueryParamValue) : value])
      .filter(([, value]) => hasQueryParamValue(value) && (!Array.isArray(value) || value.length > 0)),
  );

type VersionView = 'allVersions' | 'latestSubmission';

export default function Applications() {
  const location = useLocation();

  const { setShouldRestore, setAppDetailReportTabFocus } = useReloadRestore();
  // resets filters and tab focus if user navigates away from app guardian report and then returns
  // to app guardian report from applications list page - this is a little hacky, but it works
  useEffect(() => {
    setShouldRestore(false);
    setAppDetailReportTabFocus({ overview: 0, network: 0, root: 0 });
  }, [location.pathname, setAppDetailReportTabFocus, setShouldRestore]);

  const [accessToken = ''] = useLocalStorage('accessToken', '');
  const [resetMode, setResetMode] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  const searchParamsPageNum: number = parseInt(searchParams.get('page'), 10) || 0;
  const searchParamsPageSize: number = parseInt(searchParams.get('pageSize'), 10) || 50;

  const [page, setPage] = useState(searchParamsPageNum);
  const [pageSize, setPageSize] = useState(searchParamsPageSize);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage - 1);
  };

  const handleChangeRowsPerPage = (option: { label: string; value: number }) => {
    setPageSize(option.value);
    setPage(0);
  };

  const urlSearchQuery = searchParams.get('searchQuery') || '';
  const [localSearchQuery, setlocalSearchQuery] = useState(urlSearchQuery);

  // slow down sending the search query to the server to wait for user to finish typing by 500ms
  const [debouncedSearchQuery] = useDebounce(localSearchQuery, 500);

  useEffect(() => {
    if (debouncedSearchQuery !== urlSearchQuery) {
      const urlSearchParams = new URLSearchParams(searchParams);
      if (debouncedSearchQuery) {
        urlSearchParams.set('searchQuery', debouncedSearchQuery);
      } else {
        urlSearchParams.delete('searchQuery');
      }
      setSearchParams(urlSearchParams);
    }
  }, [debouncedSearchQuery, urlSearchQuery, searchParams, setSearchParams]);

  const defaultFilters = {
    appType: searchParams.get('appType')?.split(','),
    platform: searchParams.get('platform')?.split(','),
    categories: searchParams.get('categories')?.split(','),
    policyIds: searchParams.get('policyIds')?.split(','),
    analysisStatus: searchParams.get('analysisStatus')?.split(','),
    vendorName: searchParams.get('developerName')?.split('|'),
    startDate: searchParams.get('startDate'),
    endDate: searchParams.get('endDate'),
    riskName: searchParams.get('riskName'),
    riskSeverity: searchParams.get('riskSeverity'),
    cveId: searchParams.get('cveId'),
    cveSeverity: searchParams.get('cveSeverity'),
    installedOnly: searchParams.get('installedOnly') === 'true',
    versionView: (searchParams.get('versionView') === 'latestSubmission' ? 'latestSubmission' : 'allVersions') as VersionView,
  };

  const [appType, setAppType] = useState(defaultFilters.appType || ['all']);
  const [platform, setPlatform] = useState(defaultFilters.platform || ['all']);
  const [categoriesFilter, setCategoriesFilter] = useState(defaultFilters.categories || ['all']);
  const [policyViolations, setPolicyViolations] = useState(defaultFilters.policyIds || ['all']);
  const [analysisStatus, setAnalysisStatus] = useState(defaultFilters.analysisStatus || ['complete']);
  const [vendorName, setVendorName] = useState(defaultFilters.vendorName || ['all']);
  const [startDate, setStartDate] = useState(defaultFilters.startDate || null);
  const [endDate, setEndDate] = useState(defaultFilters.endDate || null);
  const [riskName, setRiskName] = useState(defaultFilters.riskName || '');
  const [riskSeverity, setRiskSeverity] = useState(defaultFilters.riskSeverity || '');
  const [cveId, setCveId] = useState(defaultFilters.cveId || '');
  const [cveSeverity, setCveSeverity] = useState(defaultFilters.cveSeverity || '');
  const [installedOnly, setInstalledOnly] = useState(defaultFilters.installedOnly);
  const [versionView, setVersionView] = useState<VersionView>(defaultFilters.versionView);

  const formatDateParam = (date: Date | null, boundary: 'start' | 'end'): string | undefined => {
    if (!date) return undefined;
    const day = dayjs(date);
    return (boundary === 'start' ? day.startOf('day') : day.endOf('day')).toISOString();
  };

  const {
    appNameOrder,
    toggleAppNameOrder,
    numberOfDevicesOrder,
    numberOfIorsOrder,
    toggleNumberOfIorsOrder,
    dateSubmittedOrder,
    toggleDateSubmittedOrder,
    policyViolationsOrder,
    togglePolicyViolationsOrder,
    resetToggles,
  } = useApplicationSortParams();

  const { apps, policies, totalApps, appsDataError, isLoading, mutateWithDelay } = useAppVersions(
    '/eval/applications/app-list',
    compactAppListParams({
      pageSize,
      page,
      query: debouncedSearchQuery,
      platform,
      appType,
      categories: categoriesFilter,
      policyViolations,
      analysisStatus,
      appNameOrder,
      numberOfDevicesOrder,
      numberOfIorsOrder,
      dateSubmittedOrder,
      policyViolationsOrder,
      vendorName,
      startDate,
      endDate,
      riskName,
      riskSeverity,
      cveId,
      cveSeverity,
      installedOnly: installedOnly || undefined,
      versionView: versionView === 'latestSubmission' ? versionView : undefined,
    }),
    accessToken,
  );

  const policyFilterValues = [
    { label: 'Any Status', value: 'all' },
    { label: 'Has Violations', value: 'has_violations' },
    { label: 'No Violations', value: 'no_violations' },
    ...(policies || []).map((p: PolicyDetails) => ({ label: p.policyName, value: p.policyId })),
  ];

  const { categories, categoriesError } = useCategories(accessToken);

  const formatCategories = (categoryData: string[]) => {
    const formattedCategories = categoryData
      ?.map((c: string) => c.replaceAll('_', ' ').replaceAll('AND', '&'))
      .map((c: string) => capitalize(c, true));

    const categoriesSelectData = [{ label: 'All', value: 'all' }];
    for (let i = 0; i < formattedCategories?.length; i++) {
      // reconciles any duplicates (e.g. HEALTH_AND_FITNESS, HEALTH & FITNESS) and queries by all values
      const existing = categoriesSelectData.find(e => e.label === formattedCategories[i]);
      if (existing) {
        existing.value = `${existing.value},${categories[i]}`;
        continue;
      }

      categoriesSelectData.push({
        label: formattedCategories[i],
        value: categories[i],
      });
    }

    return categoriesSelectData;
  };

  const { data: vendorNames, error: vendorNamesError } = useFetchService<string[]>('/eval/applications/developer-name-list', accessToken);

  const formattedVendorNames = (names: string[]) => {
    const vendorNamesSelectData = [{ label: 'All', value: 'all' }];
    for (let i = 0; i < names?.length; i++) {
      vendorNamesSelectData.push({
        label: names[i],
        value: names[i],
      });
    }

    return vendorNamesSelectData;
  };

  const handleStartDateChange = (date: Date | null) => {
    setStartDate(formatDateParam(date, 'start'));
    setPage(0);
  };

  const handleEndDateChange = (date: Date | null) => {
    setEndDate(formatDateParam(date, 'end'));
    setPage(0);
  };

  const { highRiskCountries, highRiskCountriesError } = useHighRiskCountries(accessToken);

  const resetFilterAndSorts = () => {
    resetToggles();
    setPage(0);
    setSearchParams();
    setResetMode(true);
  };

  useEffect(() => {
    const metaFilters = {
      platform,
      appType,
      categories: categoriesFilter,
      policyIds: policyViolations,
      analysisStatus,
      developerName: vendorName,
    };

    const filterSearchParams = {};

    if (resetMode) {
      setAppType(defaultFilters.appType || ['all']);
      setPlatform(defaultFilters.platform || ['all']);
      setCategoriesFilter(defaultFilters.categories || ['all']);
      setPolicyViolations(defaultFilters.policyIds || ['all']);
      setAnalysisStatus(defaultFilters.analysisStatus || ['complete']);
      setVendorName(defaultFilters.vendorName || ['all']);
      setStartDate(null);
      setEndDate(null);
      setRiskName('');
      setRiskSeverity('');
      setCveId('');
      setCveSeverity('');
      setInstalledOnly(false);
      setVersionView('allVersions');
      setResetMode(false);
      return;
    }

    const activeFilters = Object.entries(metaFilters).filter(filter => filter[1].length > 0);

    if (activeFilters.length) {
      activeFilters.forEach(filter => {
        const delimiter = filter[0] === 'developerName' ? '|' : ',';
        filterSearchParams[filter[0]] = Array.from(filter[1]).join(delimiter);
      });
    }

    if (localSearchQuery.trim()) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['searchQuery'] = localSearchQuery;
    }

    // if it exists, retains the orgId search param for org mismatch check (org provider)
    if (searchParams.has('orgId')) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['orgId'] = searchParams.get('orgId');
    }

    if (startDate) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['startDate'] = startDate;
    }
    if (endDate) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['endDate'] = endDate;
    }

    if (riskName) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['riskName'] = riskName;
    }

    if (riskSeverity) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['riskSeverity'] = riskSeverity;
    }

    if (cveId) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['cveId'] = cveId;
    }

    if (cveId && cveSeverity) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['cveSeverity'] = cveSeverity;
    }

    if (installedOnly) {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['installedOnly'] = 'true';
    }

    if (versionView === 'latestSubmission') {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      filterSearchParams['versionView'] = versionView;
    }

    setSearchParams(filterSearchParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    appType,
    categoriesFilter,
    platform,
    policyViolations,
    localSearchQuery,
    vendorName,
    startDate,
    endDate,
    riskName,
    riskSeverity,
    cveId,
    cveSeverity,
    installedOnly,
    versionView,
    setSearchParams,
  ]);

  return (
    <QContainer maxWidth={false}>
      <BackgroundProgressBar />
      <StyledFilterContainer>
        <KwFilterWrapper>
          <QSelect
            multiple
            label="App Type"
            options={[
              { label: 'All', value: 'all' },
              { label: 'Managed', value: 'MANAGED' },
              { label: 'Unmanaged', value: 'DISCOVERED' },
              { label: 'Requested', value: 'REQUESTED' },
            ]}
            value={appType}
            onChange={e => {
              handleUserSelection(appType, e.target.value, setAppType);
              setPage(0);
            }}
            formControlProps={{ sx: { width: 175 } }}
            selectProps={{ 'data-testid': 'app-type-select' }}
          />

          <QSelect
            multiple
            label="Platform"
            options={[
              { label: 'Both', value: 'all' },
              { label: 'iOS', value: 'ios' },
              { label: 'Android', value: 'android' },
            ]}
            value={platform}
            onChange={e => {
              handleUserSelection(platform, e.target.value, setPlatform);
              setPage(0);
            }}
            formControlProps={{ sx: { width: 175 } }}
            selectProps={{ 'data-testid': 'platform-select' }}
          />

          {!categoriesError && (
            <QSelect
              multiple
              label="Categories"
              options={formatCategories(categories)}
              value={categoriesFilter}
              onChange={e => {
                handleUserSelection(categoriesFilter, e.target.value, setCategoriesFilter);
                setPage(0);
              }}
              formControlProps={{ sx: { width: 175 } }}
              selectProps={{ 'data-testid': 'categories-select' }}
            />
          )}

          <QSelect
            multiple
            label="Policy Violations"
            options={policyFilterValues}
            value={policyViolations}
            onChange={e => {
              handleUserSelection(policyViolations, e.target.value, setPolicyViolations, ['all', 'has_violations', 'no_violations']);
              setPage(0);
            }}
            formControlProps={{ sx: { width: 175 }, disabled: policyFilterValues?.length <= 1 }}
            selectProps={{ 'data-testid': 'policy-violations-select' }}
          />
          <ResetButton
            onClick={() => {
              resetFilterAndSorts();
            }}
          />
        </KwFilterWrapper>
        <KwFilterWrapper>
          <QSelect
            multiple
            label="Status"
            options={[
              { label: 'All', value: 'all' },
              { label: 'Complete', value: 'complete' },
              { label: 'In Progress', value: 'in_progress' },
              { label: 'Unavailable', value: 'unavailable' },
            ]}
            value={analysisStatus}
            onChange={e => {
              handleUserSelection(analysisStatus, e.target.value, setAnalysisStatus);
              setPage(0);
            }}
            formControlProps={{ sx: { width: 175 } }}
            selectProps={{ 'data-testid': 'status-select' }}
          />
          {!vendorNamesError && (
            <QSelect
              multiple
              label="Vendor"
              options={formattedVendorNames(vendorNames)}
              value={vendorName}
              onChange={e => {
                handleUserSelection(vendorName, e.target.value, setVendorName);
                setPage(0);
              }}
              formControlProps={{ sx: { width: 175 } }}
              selectProps={{ 'data-testid': 'vendor-select' }}
            />
          )}
          <Box sx={{ width: 175 }} data-testid="date-submitted-start">
            <QDatePicker
              label="Date Submitted Start"
              date={startDate ? dayjs(startDate).toDate() : null}
              handleDateChange={handleStartDateChange}
              maxDate={new Date()}
            />
          </Box>
          <Box sx={{ width: 175 }} data-testid="date-submitted-end">
            <QDatePicker
              label="Date Submitted End"
              date={endDate ? dayjs(endDate).toDate() : null}
              maxDate={new Date()}
              handleDateChange={handleEndDateChange}
            />
          </Box>
        </KwFilterWrapper>
      </StyledFilterContainer>

      {riskName && (
        <Box sx={{ mt: 2, mb: 1 }}>
          <QChip
            label={riskName}
            onDelete={() => {
              setRiskName('');
              setRiskSeverity('');
            }}
            sx={{ fontSize: '14px', padding: '8px 4px', height: 'auto' }}
          />
        </Box>
      )}

      {cveId && (
        <Box sx={{ mt: 2, mb: 1 }}>
          <QChip
            label={cveId}
            onDelete={() => {
              setCveId('');
              setCveSeverity('');
            }}
            sx={{ fontSize: '14px', padding: '8px 4px', height: 'auto' }}
          />
        </Box>
      )}

      <StyledListViewControls>
        <StyledVersionViewControl>
          <StyledVersionViewToggleButtonGroup
            exclusive
            value={versionView}
            onChange={(_event, newVersionView: VersionView | null) => {
              if (newVersionView) {
                setVersionView(newVersionView);
                setPage(0);
              }
            }}
            aria-label="Application version view"
          >
            <QToggleButton value="allVersions" aria-label="All Versions">
              All Versions
            </QToggleButton>
            <QToggleButton value="latestSubmission" aria-label="Latest Submission">
              Latest Submission
            </QToggleButton>
          </StyledVersionViewToggleButtonGroup>
          <QTooltip
            title="Latest Submission shows only the most recently submitted version of each app in your environment. Those versions may not match what's currently in the app store."
            arrow
            componentsProps={{ tooltip: { sx: { padding: '8px 16px' } } }}
          >
            <QIconButton aria-label="About Latest Submission" size="small">
              <InfoOutlinedIcon fontSize="small" />
            </QIconButton>
          </QTooltip>
        </StyledVersionViewControl>

        <StyledInstalledOnlyControl>
          <QSwitch
            checked={installedOnly}
            onChange={event => {
              setInstalledOnly(event.target.checked);
              setPage(0);
            }}
            inputProps={{ 'aria-label': 'Installed only' }}
          />
          <QTypography variant="bodyBold">Installed only</QTypography>
          <QTooltip
            title="Show only apps installed on 1 or more devices in your environment."
            arrow
            componentsProps={{ tooltip: { sx: { padding: '8px 16px' } } }}
          >
            <StyledInstalledInfoButton aria-label="About Installed only" size="small">
              <InfoOutlinedIcon fontSize="small" />
            </StyledInstalledInfoButton>
          </QTooltip>
        </StyledInstalledOnlyControl>
      </StyledListViewControls>

      <ApplicationsTable
        apps={apps}
        total={totalApps}
        appsDataError={appsDataError}
        isLoading={isLoading}
        highRiskCountries={highRiskCountries}
        highRiskCountriesError={highRiskCountriesError}
        searchQuery={localSearchQuery}
        setSearchQuery={setlocalSearchQuery}
        appNameOrder={appNameOrder}
        toggleAppNameOrder={toggleAppNameOrder}
        numberOfIorsOrder={numberOfIorsOrder}
        toggleNumberOfIorsOrder={toggleNumberOfIorsOrder}
        dateSubmittedOrder={dateSubmittedOrder}
        toggleDateSubmittedOrder={toggleDateSubmittedOrder}
        policyViolationsOrder={policyViolationsOrder}
        togglePolicyViolationsOrder={togglePolicyViolationsOrder}
        page={page}
        pageSize={pageSize}
        setPage={setPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        backQParams={searchParams.toString()}
        fetchAppList={() => mutateWithDelay(5000)}
      />
    </QContainer>
  );
}

export const StyledCardHeader = styled('div')({
  fontSize: '18px',
  lineHeight: '25px',
  fontWeight: '500',
  color: '#000',
});

const StyledFilterContainer = styled('div')`
  min-width: 640px;
`;

const StyledListViewControls = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  min-width: 640px;
  margin: 24px 0 16px;
`;

const StyledVersionViewControl = styled('div')`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const StyledVersionViewToggleButtonGroup = styled(QToggleButtonGroup)`
  && .MuiToggleButtonGroup-grouped {
    color: #000;
    font-family: 'Nunito Sans', sans-serif;
    font-size: 13px;
    font-weight: 700;
  }

  && .MuiToggleButtonGroup-grouped.Mui-selected {
    border: 1px solid #757575;
    color: #000;
  }
`;

const StyledInstalledOnlyControl = styled('div')`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const StyledInstalledInfoButton = styled(QIconButton)`
  margin-left: 8px;
`;
